/**
 * Базовый fetch-клиент для forms-backend.
 * credentials: 'include' — сессионная cookie пробрасывается автоматически.
 * При 401 — кидаем специальную ошибку; обработка — в store/auth.
 */

import { handleError, handleUnauthorized } from './error-handler';

/**
 * Получить CSRF-токен из cookie (Double Submit Cookie).
 * Cookie tnf_csrf не HttpOnly — доступна из JavaScript.
 */
function getCSRFCookie(): string {
  const match = document.cookie.match(/(?:^|;\s*)tnf_csrf=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : '';
}

/**
 * Нужно ли добавлять X-CSRF-Token header.
 * GET/HEAD/OPTIONS — безопасные методы.
 * Публичные эндпоинты и auth — CSRF не нужен.
 */
function shouldIncludeCSRF(method: string, url: string): boolean {
  if (['GET', 'HEAD', 'OPTIONS'].includes(method)) return false;
  if (url.startsWith('/api/v1/public/')) return false;
  if (url.startsWith('/auth/')) return false;
  if (url === '/healthz' || url === '/readyz') return false;
  return true;
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export class UnauthorizedError extends ApiError {
  constructor() {
    super(401, 'Unauthorized');
    this.name = 'UnauthorizedError';
  }
}

export interface ApiFetchOptions extends RequestInit {
  /** Отключить глобальную обработку ошибок (toast, redirect) */
  silentError?: boolean;
  /** Количество повторных попыток при сетевых ошибках или 5xx (по умолчанию: 3) */
  retries?: number;
  /** Начальная задержка между попытками в мс (по умолчанию: 1000) */
  retryDelay?: number;
  /** Включить deduplication и кэширование для GET-запросов (по умолчанию: true) */
  deduplicate?: boolean;
}

// Кэш для GET-запросов
const cache = new Map<string, { data: unknown; timestamp: number }>();
const CACHE_TTL = 5000; // 5 секунд

// Pending запросы для deduplication
const pendingRequests = new Map<string, Promise<unknown>>();

/**
 * Очистить кэш и pending запросы (для тестов).
 */
export function clearCache(): void {
  cache.clear();
  pendingRequests.clear();
}

/**
 * Инвалидировать кэш для URL.
 * Вызывается при мутациях (POST/PUT/PATCH/DELETE).
 */
function invalidateCache(url: string): void {
  // Инвалидируем все ключи, связанные с этим URL
  for (const key of cache.keys()) {
    if (key.endsWith(`:${url}`)) {
      cache.delete(key);
    }
  }
}

/**
 * Задержка перед повторной попыткой (exponential backoff).
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Проверить, нужно ли повторять запрос.
 * Повторяем при сетевых ошибках и 5xx (кроме 501).
 */
function shouldRetry(error: unknown, status?: number): boolean {
  // Сетевые ошибки (TypeError: Failed to fetch)
  if (error instanceof TypeError) {
    return true;
  }
  // 5xx ошибки (кроме 501 Not Implemented)
  if (status && status >= 500 && status < 600 && status !== 501) {
    return true;
  }
  return false;
}

export async function apiFetch<T>(
  url: string,
  options: ApiFetchOptions = {}
): Promise<T> {
  const { silentError, retries = 3, retryDelay = 1000, deduplicate = true, ...fetchOptions } = options;
  const method = (fetchOptions.method || 'GET').toUpperCase();
  const cacheKey = `${method}:${url}`;

  // Deduplication и кэширование только для GET-запросов
  if (method === 'GET' && deduplicate) {
    // Проверяем кэш
    const cached = cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data as T;
    }

    // Проверяем pending запросы
    const pending = pendingRequests.get(cacheKey);
    if (pending) {
      return pending as Promise<T>;
    }
  }

  // Функция выполнения запроса
  const executeRequest = async (): Promise<T> => {
    let lastError: unknown;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const res = await fetch(url, {
          ...fetchOptions,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            // CSRF: только для state-changing запросов не на public/auth эндпоинты
            ...(shouldIncludeCSRF(method, url) ? { 'X-CSRF-Token': getCSRFCookie() } : {}),
            ...(fetchOptions.headers ?? {}),
          },
        });

        if (res.status === 401) {
          if (!silentError) {
            handleUnauthorized();
          }
          throw new UnauthorizedError();
        }

        if (!res.ok) {
          const text = await res.text().catch(() => res.statusText);
          const error = new ApiError(res.status, text);

          // Проверяем, нужно ли повторять
          if (attempt < retries && shouldRetry(error, res.status)) {
            lastError = error;
            // Exponential backoff: 1s, 2s, 4s, 8s...
            await delay(retryDelay * Math.pow(2, attempt));
            continue;
          }

          if (!silentError) {
            handleError(error, url);
          }

          throw error;
        }

        // 204 No Content
        if (res.status === 204) {
          return undefined as unknown as T;
        }

        return res.json() as Promise<T>;
      } catch (error) {
        // Если это UnauthorizedError, не повторяем
        if (error instanceof UnauthorizedError) {
          throw error;
        }

        // Проверяем, нужно ли повторять
        if (attempt < retries && shouldRetry(error)) {
          lastError = error;
          // Exponential backoff: 1s, 2s, 4s, 8s...
          await delay(retryDelay * Math.pow(2, attempt));
          continue;
        }

        // Если это ApiError и уже обработан, просто пробрасываем
        if (error instanceof ApiError) {
          throw error;
        }

        // Сетевая ошибка — логируем и пробрасываем
        if (!silentError) {
          handleError(error instanceof Error ? error : new Error(String(error)), url);
        }

        throw error;
      }
    }

    // Если все попытки исчерпаны
    throw lastError instanceof Error ? lastError : new Error(String(lastError));
  };

  // Для GET-запросов с deduplication
  if (method === 'GET' && deduplicate) {
    const request = executeRequest().then(
      (data) => {
        // Сохраняем в кэш
        cache.set(cacheKey, { data, timestamp: Date.now() });
        // Удаляем из pending
        pendingRequests.delete(cacheKey);
        return data;
      },
      (error) => {
        // При ошибке удаляем из pending
        pendingRequests.delete(cacheKey);
        throw error;
      }
    );

    // Сохраняем в pending
    pendingRequests.set(cacheKey, request);

    return request;
  }

  // Для не-GET запросов или deduplicate=false
  // Инвалидируем кэш при мутациях
  if (method !== 'GET') {
    invalidateCache(url);
  }

  return executeRequest();
}
